import type {
  ResourceVersion,
  MessageMutationOrdering,
  PendingMessageVersions,
} from '../../types/event-order.types';
import type { QueryClient } from '@tanstack/react-query';
import { FunnelInteractionType } from 'src/api/generated';
import type {
  CommunicationsResourceChangedNotification,
  FunnelChannelSession,
  FunnelConversation,
  FunnelConversationDocument,
  Message,
  TCSearcherTimelineItemDocument,
} from 'src/api/generated';
import {
  getModelFromCache,
  type InfiniteQueryData,
} from 'src/modules/shared/utils/cache/update.util';
import type { TCRealtimePayload } from '../../types/realtime-payload.types';
import { getConversationRecoveryMinimum } from 'src/modules/shared/communications/utils/api/conversation-recovery-query.util';

const versions = new WeakMap<QueryClient, Map<string, ResourceVersion>>();
const MAX_RESOURCE_VERSIONS = 2000;
const MAX_PENDING_MESSAGE_VERSIONS = 2000;

const pendingMessageVersions = new WeakMap<
  QueryClient,
  Set<PendingMessageVersions>
>();

/**
 * Retains relevant message ordering until one create request settles. Resource
 * IDs may be unknown until its response, including snapshot-free deletions.
 * Storage is capped per request; saturation conservatively refuses HTTP writes.
 * @param queryClient - Cache owner shared by socket consumers and mutations.
 * @param scope - Known conversation identities for the pending send.
 * @returns Response admission and an idempotent settlement cleanup function.
 */
export const beginMessageMutationOrdering = (
  queryClient: QueryClient,
  scope: Pick<Message, 'funnelConversationId' | 'conversationId'>
): MessageMutationOrdering => {
  const pending = pendingMessageVersions.get(queryClient) ?? new Set();
  pendingMessageVersions.set(queryClient, pending);
  const state: PendingMessageVersions = {
    funnelConversationId: scope.funnelConversationId,
    conversationId: scope.conversationId,
    versions: new Map(),
    recoveryRequired: false,
  };
  pending.add(state);
  let disposed = false;
  return {
    canApply: (message) => {
      // Optimistic scope is a hint: a response outside it needs recovery because
      // its events may not have been retained by this request.
      if (
        (state.funnelConversationId &&
          state.funnelConversationId !== message.funnelConversationId) ||
        (state.conversationId &&
          state.conversationId !== message.conversationId)
      ) {
        state.recoveryRequired = true;
      }
      const previous = state.versions.get(
        `${message.tenantId}:Message:${message.id}`
      );
      return (
        !disposed &&
        !state.recoveryRequired &&
        (!previous || Date.parse(message.updatedAt) > previous.timestamp)
      );
    },
    needsRecovery: () => state.recoveryRequired,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      state.versions.clear();
      pending.delete(state);
      if (pending.size === 0) pendingMessageVersions.delete(queryClient);
    },
  };
};

const retainPendingMessageVersion = (
  queryClient: QueryClient,
  payload: TCRealtimePayload,
  key: string,
  version: ResourceVersion
): void => {
  if (payload.resourceType !== 'Message') return;
  pendingMessageVersions.get(queryClient)?.forEach((state) => {
    if (
      (state.funnelConversationId &&
        state.funnelConversationId !== payload.funnelConversationId) ||
      (state.conversationId &&
        payload.messagingConversationId &&
        state.conversationId !== payload.messagingConversationId)
    ) {
      return;
    }
    if (
      !state.versions.has(key) &&
      state.versions.size >= MAX_PENDING_MESSAGE_VERSIONS
    ) {
      // Keep previously retained authority; never evict a pending tombstone.
      state.recoveryRequired = true;
      return;
    }
    state.versions.set(key, version);
  });
};

const getRetainedVersion = (
  queryClient: QueryClient,
  key: string
): ResourceVersion | undefined => {
  let latest = versions.get(queryClient)?.get(key);
  pendingMessageVersions.get(queryClient)?.forEach((state) => {
    const retained = state.versions.get(key);
    if (
      retained &&
      (!latest ||
        retained.timestamp > latest.timestamp ||
        (retained.timestamp === latest.timestamp && retained.deleted))
    ) {
      latest = retained;
    }
  });
  return latest;
};

const getCachedTimestamp = (
  queryClient: QueryClient,
  payload: Pick<TCRealtimePayload, 'tenantId' | 'resourceType' | 'resourceId'>
): number => {
  if (payload.resourceType === 'Conversation') {
    const cached = getModelFromCache<FunnelConversation>(
      queryClient,
      ['crm/funnel-conversations'],
      payload.resourceId
    );
    return Math.max(
      Date.parse(cached?.updatedAt ?? '') || 0,
      getConversationRecoveryMinimum(
        queryClient,
        Number(payload.resourceId),
        payload.tenantId
      )
    );
  }
  if (payload.resourceType === 'ChannelSession') {
    const cached = getModelFromCache<FunnelChannelSession>(
      queryClient,
      ['crm/funnel-channel-sessions'],
      payload.resourceId
    );
    return Date.parse(cached?.updatedAt ?? '') || 0;
  }

  let timestamp = 0;
  if (payload.resourceType === 'Message') {
    const cached = queryClient.getQueryData<Message>([
      'messaging/messages',
      payload.resourceId,
    ]);
    timestamp = Date.parse(cached?.updatedAt ?? '') || 0;
  }
  const timelines = queryClient.getQueriesData<
    InfiniteQueryData<TCSearcherTimelineItemDocument>
  >({ queryKey: ['crm/timeline-items/search'] });
  for (const [, data] of timelines) {
    for (const page of data?.pages ?? []) {
      for (const item of page.items) {
        if (
          item.modelType === payload.resourceType &&
          item.id === payload.resourceId &&
          item.tenantId === payload.tenantId
        ) {
          timestamp = Math.max(timestamp, Date.parse(item.updatedAt) || 0);
        }
      }
    }
  }
  return timestamp;
};

/**
 * Rejects duplicate and older events, including delayed creates after deletes.
 * Versions belong to the QueryClient so multiple mounted topic consumers agree.
 * Cached GET snapshots also protect state when a view first starts listening.
 *
 * @param queryClient - Owner of the resource caches.
 * @param payload - Validated lifecycle event or compact recovery notification.
 * @returns Whether the event can advance the cached resource state.
 */
export const acceptRealtimeResourceVersion = (
  queryClient: QueryClient,
  payload: TCRealtimePayload | CommunicationsResourceChangedNotification
): boolean => {
  let resourceVersions = versions.get(queryClient);
  if (!resourceVersions) {
    resourceVersions = new Map();
    versions.set(queryClient, resourceVersions);
  }
  const key = `${payload.tenantId}:${payload.resourceType}:${payload.resourceId}`;
  const previous = getRetainedVersion(queryClient, key);
  const timestamp = Date.parse(payload.occurredAt);
  const cachedTimestamp = getCachedTimestamp(queryClient, payload);
  if (
    timestamp < cachedTimestamp ||
    (previous &&
      (previous.eventId === payload.eventId ||
        timestamp < previous.timestamp ||
        (timestamp === previous.timestamp && previous.deleted)))
  ) {
    return false;
  }
  resourceVersions.delete(key);
  const version = {
    timestamp,
    deleted: 'action' in payload && payload.action === 'deleted',
    eventId: payload.eventId,
  };
  resourceVersions.set(key, version);
  if ('action' in payload) {
    retainPendingMessageVersion(queryClient, payload, key, version);
  }
  if (resourceVersions.size > MAX_RESOURCE_VERSIONS) {
    const oldestKey = resourceVersions.keys().next().value;
    if (oldestKey !== undefined) resourceVersions.delete(oldestKey);
  }
  return true;
};

/**
 * Detects ambiguous Conversation, Call and ChannelSession snapshots without guessing socket/HTTP order.
 * A board revision also covers non-Call changes, so an equal/newer same-Call
 * preview requires HTTP recovery rather than rejecting legitimate Call progress.
 * @param queryClient - Owner of the accepted revision registry.
 * @param payload - Validated canonical lifecycle event.
 * @returns Whether canonical HTTP must disambiguate this update.
 */
export const isAmbiguousResourceVersion = (
  queryClient: QueryClient,
  payload: TCRealtimePayload
): boolean => {
  if (
    !['Conversation', 'Call', 'ChannelSession'].includes(
      payload.resourceType
    ) ||
    payload.action === 'deleted'
  ) {
    return false;
  }
  const timestamp = Date.parse(payload.occurredAt);
  if (payload.resourceType === 'Call') {
    const boards = queryClient.getQueriesData<
      InfiniteQueryData<FunnelConversationDocument>
    >({ queryKey: ['crm/documents/funnel-conversations'] });
    for (const [, data] of boards) {
      if (
        data?.pages.some((page) =>
          page.items.some(
            (document) =>
              document.id === String(payload.funnelConversationId) &&
              document.tenantId === payload.tenantId &&
              document.lastInteraction?.modelType ===
                FunnelInteractionType.CALL &&
              document.lastInteraction.id === payload.resourceId &&
              Date.parse(document.updatedAt) >= timestamp
          )
        )
      ) {
        return true;
      }
    }
  }
  const previous = getRetainedVersion(
    queryClient,
    `${payload.tenantId}:${payload.resourceType}:${payload.resourceId}`
  );
  return (
    getCachedTimestamp(queryClient, payload) === timestamp ||
    (previous !== undefined &&
      !previous.deleted &&
      previous.timestamp === timestamp &&
      previous.eventId !== payload.eventId)
  );
};

/**
 * Checks delayed mutation responses against the same registry used by all topic
 * consumers. Equal-version realtime snapshots and deletion tombstones win.
 * @param queryClient - Owner of accepted realtime versions and HTTP caches.
 * @param message - Actual server response, never an optimistic placeholder.
 * @param ordering - Lifetime-bound authority for an active create request.
 * @returns Whether the response can update the message timeline.
 */
export const canApplyMessageMutationSnapshot = (
  queryClient: QueryClient,
  message: Message,
  ordering?: MessageMutationOrdering
): boolean => {
  if (ordering && !ordering.canApply(message)) return false;
  const timestamp = Date.parse(message.updatedAt);
  if (!Number.isFinite(timestamp)) return false;
  const previous = getRetainedVersion(
    queryClient,
    `${message.tenantId}:Message:${message.id}`
  );
  if (previous && timestamp <= previous.timestamp) return false;
  return (
    timestamp >=
    getCachedTimestamp(queryClient, {
      tenantId: message.tenantId,
      resourceType: 'Message',
      resourceId: message.id,
    })
  );
};
